import { FC, useEffect, useState } from 'react';
import * as tsClient from '@tsClient';
import useNoodleApi from '@hooks/useNoodleApi';
import ProgressIndicator from '@components/ProgressIndicator';
import Header from '@components/DesignLibrary/Header';
import * as format from '@format';
import { FixedSizeList as List } from 'react-window';
import CollapsibleArea from '@components/CollapsibleArea';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { Table, THead, TH, TD, TableRow, TBody } from '@/components/DesignLibrary/Table';
import findArrayInJSON from '@/helpers/findArrayInJSON';
import CopyButton from '@/components/CopyButton';

type Props = {
  dataReferenceId: string;
};

const DataReferenceDetail: FC<Props> = ({ dataReferenceId }) => {
  const [jsonLines, setJsonLines] = useState<string[] | null>(null);
  const [jsonData, setJsonData] = useState('');
  const [tableData, setTableData] = useState<unknown[] | null>(null);

  const { data: dataReference, getData: getDataReference } = useNoodleApi(tsClient.questionnaires.getDataReferenceBySlug);

  useEffect(() => {
    getDataReference({ slug: dataReferenceId });
  }, [dataReferenceId, getDataReference]);

  useEffect(() => {
    if (dataReference?.data) {
      const data = typeof dataReference.data === 'string' ? JSON.parse(dataReference.data) : dataReference.data;
      setJsonData(JSON.stringify(data));
      setJsonLines(JSON.stringify(data, null, 2).split('\n'));

      const array = findArrayInJSON(data);
      if (array) setTableData(array);
    }
  }, [dataReference]);

  const Row = ({ index, style }: { index: number; style: React.CSSProperties }): JSX.Element => (
    <div style={style}>
      <SyntaxHighlighter
        language="json"
        customStyle={{
          backgroundColor: 'var(--color-gray-0)',
          fontSize: 14,
          lineHeight: 0,
          margin: 0,
          overflow: 'hidden',
          padding: '8px 16px',
          width: '100%',
        }}
      >
        {jsonLines ? jsonLines[index] : ''}
      </SyntaxHighlighter>
    </div>
  );

  if (!dataReference || !jsonLines) {
    return <ProgressIndicator isCentered isPage />;
  }

  return (
    <div>
      <Header
        title={`${dataReference.label}: ${dataReference.versionLabel}`}
        description={`Last updated: ${format.datetimeLocalTZ.monthDay(dataReference.date)}`}
      />
      <CollapsibleArea title="Table">
        {tableData && (
          <Table>
            <THead>
              <TableRow>
                {Object.keys(tableData[0] || {}).map((key) => (
                  <TH key={key} label={key} />
                ))}
              </TableRow>
            </THead>
            <TBody>
              {tableData.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {(typeof row === 'object' && row !== null ? Object.values(row) : []).map((value, colIndex) => (
                    <TD compact key={colIndex} label={String(value)} />
                  ))}
                </TableRow>
              ))}
            </TBody>
          </Table>
        )}
      </CollapsibleArea>
      <CollapsibleArea
        title='JSON'
        open
      >
        <div style={{ position: 'relative' }}>
          <CopyButton text={jsonData} style={{ position: 'absolute', right: 'var(--spacer-sm)', top: 'var(--spacer-sm)' }}>
            Copy
          </CopyButton>
          <List
            height={800}
            itemCount={jsonLines.length}
            itemSize={28}
            width="100%"
          >
            {Row}
          </List>
        </div>
      </CollapsibleArea>
    </div>
  );
};

export default DataReferenceDetail;
